import React, { useEffect, useRef, useState } from "react";
import { QRCodeSVG, QRCodeCanvas } from "qrcode.react";
import { X, Copy, Share2, MessageCircle, Send, Facebook, Mail, Check, Download, Image as ImageIcon, MapPin, Store } from "lucide-react";
import { toast } from "sonner";
import { toPng } from "html-to-image";
import { resolveImageUrl } from "@/lib/api";

/**
 * Shop Share experience — reuses the existing QR flow and layers a visually
 * rich, downloadable Shop Share Card on top. Vendors can share the card to
 * WhatsApp Status / Instagram / social feeds; the QR always encodes the
 * canonical public /v/{slug} URL of this exact shop.
 *
 * Props:
 *  - open, onClose
 *  - shopName, tagline, shareUrl (/api/share/{slug}), profileUrl (/v/{slug})
 *  - shop (optional) — the full vendor profile object for a richer card
 */
// Optional for other share kinds (e.g. a service): heading, noun ("shop"/"service") and a custom card body that includes its own QR.
export default function ShareSheet({ open, onClose, shopName, tagline, shareUrl, profileUrl, shop, heading = "Share this shop", noun = "shop", card = null }) {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const cardRef = useRef(null);
  const qrRef = useRef(null);
  const [sharing, setSharing] = useState(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Back button / Escape close the sheet; a history entry is pushed so Back pops it instead of leaving the page.
  useEffect(() => {
    if (!open) return undefined;
    if (!window.history.state?.verbShare) window.history.pushState({ ...window.history.state, verbShare: true }, "");
    const onPop = () => onCloseRef.current?.();
    const onKey = (e) => { if (e.key === "Escape") close(); };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden"; // no background scrolling while the card is open
    window.addEventListener("popstate", onPop);
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prevOverflow; window.removeEventListener("popstate", onPop); window.removeEventListener("keydown", onKey); };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = () => {
    if (window.history.state?.verbShare) window.history.back();
    else onCloseRef.current?.();
  };
  if (!open) return null;

  const message = `${shopName} on VerB — ${tagline || `local ${noun}`}\n${shareUrl}`;

  const copy = async (text, label = "Link") => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed — long-press the link");
    }
  };

  const safeName = (shopName || `verb-${noun}`).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || noun;
  const toFile = async (dataUrl, name) => new File([await (await fetch(dataUrl)).blob()], name, { type: "image/png" });
  const saveFile = (file) => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(file); a.download = file.name;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 1500);
  };
  const buildFiles = async () => {
    const files = [];
    try {
      files.push(await toFile(await toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#ffffff" }), `verb-${safeName}-share-card.png`));
    } catch { toast.error(`${noun === "shop" ? "Shop" : "Service"} card image could not be created — sharing the QR and link only`); }
    if (qrRef.current) files.push(await toFile(qrRef.current.toDataURL("image/png"), `verb-${safeName}-qr.png`));
    return files;
  };

  // Share card + QR images with the link when the device supports file sharing; otherwise download them and share/copy the link.
  const nativeShare = async () => {
    setSharing(true);
    const text = `${shopName} on VerB — ${tagline || `local ${noun}`}. Scan the QR or open: ${shareUrl}`;
    try {
      const files = await buildFiles();
      if (files.length && navigator.canShare?.({ files })) {
        try {
          await navigator.share({ title: shopName, text, url: shareUrl, files });
          toast.success(files.length > 1 ? `Shared ${noun} card, QR code and link` : "Shared QR code and link");
        } catch (e) { if (e?.name !== "AbortError") toast.error("Sharing failed — please try again"); }
        return;
      }
      files.forEach(saveFile);
      if (navigator.share) {
        try {
          await navigator.share({ title: shopName, text, url: shareUrl });
          toast.info(`This browser can't share images, so only the link was shared. The ${noun} card and QR were downloaded — attach them from your gallery/downloads.`, { duration: 8000 });
        } catch (e) { if (e?.name !== "AbortError") toast.error("Sharing failed — the link is below to copy"); }
      } else {
        await copy(shareUrl, "Link");
        toast.info(`Sharing isn't supported here. The ${noun} card and QR were downloaded and the link was copied — paste it in WhatsApp with the images.`, { duration: 8000 });
      }
    } finally { setSharing(false); }
  };

  const downloadCard = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      // Rasterise at 2x so it looks sharp on social feeds / print.
      const dataUrl = await toPng(cardRef.current, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: "#ffffff",
        filter: (node) => {
          // Skip external images that can't be inlined (CORS) — we handle them separately
          return true;
        },
      });
      const link = document.createElement("a");
      link.download = `verb-${safeName}-share-card.png`;
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${noun === "shop" ? "Shop" : "Service"} card downloaded`);
    } catch (err) {
      toast.error("Could not generate the card image");
    } finally { setDownloading(false); }
  };

  const targets = [
    { label: "WhatsApp", icon: MessageCircle, color: "bg-emerald-500 hover:bg-emerald-600", href: `https://wa.me/?text=${encodeURIComponent(message)}`, testid: "share-whatsapp" },
    { label: "Telegram", icon: Send, color: "bg-sky-500 hover:bg-sky-600", href: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shopName)}`, testid: "share-telegram" },
    { label: "Facebook", icon: Facebook, color: "bg-blue-600 hover:bg-blue-700", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, testid: "share-facebook" },
    { label: "SMS", icon: MessageCircle, color: "bg-slate-700 hover:bg-slate-800", href: `sms:?&body=${encodeURIComponent(message)}`, testid: "share-sms" },
    { label: "Email", icon: Mail, color: "bg-amber-600 hover:bg-amber-700", href: `mailto:?subject=${encodeURIComponent(shopName)}&body=${encodeURIComponent(message)}`, testid: "share-email" },
  ];

  // Derived shop fields (fall back to top-level props when a full shop object isn't passed in)
  const s = shop || {};
  const category = (s.categories && s.categories[0]) || s.category || "";
  const address = s.address || "";
  const pincode = s.pincode || "";
  const statusLabel = s.status?.label || (s.status?.is_open ? "Open now" : "Closed");
  const isOpen = !!s.status?.is_open;
  const imageUrl = s.logo || s.image ? resolveImageUrl(s.logo || s.image) : "";

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-3 sm:p-6 bg-slate-900 overflow-y-auto overscroll-contain" onClick={close} data-testid="share-sheet">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white px-5 py-4 border-b border-slate-100 flex items-center justify-between z-10">
          <div className="min-w-0">
            <h3 className="font-display text-lg font-bold">{heading}</h3>
            <p className="text-xs text-slate-500 truncate">{shopName}</p>
          </div>
          <button onClick={close} className="p-2 rounded-full hover:bg-slate-100" aria-label="Close" data-testid="share-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 sm:p-5 space-y-4">
          {/* ------- Shop Share Card (visual + downloadable) ------- */}
          <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm" data-testid={`${noun}-share-card-wrap`}>
            {card ? <div ref={cardRef} data-testid={`${noun}-share-card`}>{card}</div> : <div
              ref={cardRef}
              className="relative bg-gradient-to-br from-emerald-50 via-white to-amber-50 p-4 pb-5"
              data-testid="shop-share-card"
            >
              {/* Brand strip */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-baseline gap-1.5">
                  <span className="font-display text-xl font-black tracking-tight text-emerald-800">VerB</span>
                  <span className="text-[9px] tracking-[0.22em] text-slate-500 font-semibold">Vertual Bazaar</span>
                </div>
                {s.status && (
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${isOpen ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-700"}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${isOpen ? "bg-emerald-500" : "bg-rose-500"}`} />
                    {statusLabel}
                  </span>
                )}
              </div>

              {/* Hero: shop image + core info */}
              <div className="flex gap-3">
                <div className="w-20 h-20 rounded-2xl overflow-hidden bg-slate-200 flex-shrink-0 border-2 border-white shadow">
                  {imageUrl ? (
                    <img src={imageUrl} alt={shopName} crossOrigin="anonymous" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-emerald-100 to-amber-50 flex items-center justify-center">
                      <Store className="w-8 h-8 text-emerald-600" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg sm:text-xl font-black text-slate-900 leading-tight line-clamp-2" data-testid="card-shop-name">{shopName}</div>
                  {category && <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-medium mt-0.5" data-testid="card-shop-category">{category}</div>}
                  {tagline && <div className="text-[11px] text-slate-600 mt-1 line-clamp-2">{tagline}</div>}
                </div>
              </div>

              {/* Address line */}
              {(address || pincode) && (
                <div className="mt-3 flex items-start gap-1.5 text-[11px] text-slate-600">
                  <MapPin className="w-3.5 h-3.5 text-emerald-700 flex-shrink-0 mt-0.5" />
                  <div className="line-clamp-2"><span className="truncate">{address}</span>{pincode && <span className="text-slate-700"> · PIN {pincode}</span>}</div>
                </div>
              )}

              {/* QR + CTA */}
              <div className="mt-4 flex items-center gap-3 rounded-xl bg-white border border-emerald-100 p-3">
                <div className="bg-white p-1.5 rounded-lg border border-slate-100" data-testid="share-qr">
                  <QRCodeSVG value={profileUrl || shareUrl} size={92} bgColor="#ffffff" fgColor="#065f46" level="M" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-slate-900 leading-tight" data-testid="card-qr-heading">Scan to visit shop</div>
                  <div className="text-[11px] font-normal text-slate-600 leading-tight mt-0.5" data-testid="card-qr-body">Open with any camera</div>
                  <div className="text-[10px] text-slate-500 mt-1 truncate" data-testid="card-share-url">{profileUrl}</div>
                </div>
              </div>

              {/* Footer brand */}
              <div className="mt-3 pt-2 border-t border-emerald-100 flex items-center justify-between text-[10px] text-slate-500">
                <span>verb.app · Local Vertual Bazaar</span>
                <span className="text-emerald-800">Shop with your neighbourhood</span>
              </div>
            </div>}
          </div>

          <button onClick={downloadCard} disabled={downloading} className="w-full inline-flex items-center justify-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-full py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="share-download-card">
            {downloading ? <ImageIcon className="w-4 h-4 animate-pulse" /> : <Download className="w-4 h-4" />}
            {downloading ? "Preparing..." : `Download ${noun} card`}
          </button>

          {/* Copy link */}
          <div className="flex items-center gap-2 border border-slate-200 rounded-xl p-2">
            <div className="flex-1 min-w-0 text-xs sm:text-sm text-slate-600 truncate px-2" data-testid="share-url-text">{shareUrl}</div>
            <button onClick={() => copy(shareUrl, "Link")} className="inline-flex items-center gap-1 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg px-3 py-2 text-xs font-semibold flex-shrink-0" data-testid="share-copy-link">
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? "Copied" : "Copy"}
            </button>
          </div>

          {/* Native share */}
          <button onClick={nativeShare} disabled={sharing} className="w-full inline-flex items-center justify-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl py-3 text-sm font-semibold disabled:opacity-60" data-testid="share-native">
            <Share2 className="w-4 h-4" /> {sharing ? "Preparing card & QR…" : "Share card, QR & link"}
          </button>
          <QRCodeCanvas ref={qrRef} value={profileUrl || shareUrl} size={720} level="M" marginSize={4} style={{ display: "none" }} />

          {/* App targets */}
          <div className="grid grid-cols-3 min-[375px]:grid-cols-5 gap-2">
            {targets.map((t) => {
              const Icon = t.icon;
              return (
                <a key={t.label} href={t.href} target="_blank" rel="noopener noreferrer" className={`flex flex-col items-center gap-1 text-white rounded-xl px-1 py-3 text-[10px] font-semibold ${t.color}`} data-testid={t.testid}>
                  <Icon className="w-4 h-4" /> {t.label}
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
